import { type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { AlertTriangle, Inbox, Loader, Square } from 'lucide-react';
import { formatNumber, messages } from '@nesc/shared';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useState } from 'react';

/**
 * 空状態（docs/09-design-system.md 第6章）。
 * 現状＋次の行動を示す。条件に一致しない場合とデータが 0 件の場合を出し分ける。
 */
export const EmptyState = ({
  title,
  description,
  action,
  icon,
  className,
}: {
  title: string;
  description?: string;
  action?: { label: string; onClick: () => void };
  icon?: ReactNode;
  className?: string;
}) => (
  <Card className={cn('flex flex-col items-center gap-3 px-6 py-12 text-center', className)}>
    <div className="text-foreground-subtle" aria-hidden>
      {icon ?? <Inbox className="size-8" />}
    </div>
    <div className="space-y-1">
      <p className="text-h3 text-foreground">{title}</p>
      {description && <p className="text-body text-foreground-muted">{description}</p>}
    </div>
    {action && (
      <Button size="lg" onClick={action.onClick}>
        {action.label}
      </Button>
    )}
  </Card>
);

/**
 * 破壊的操作の確認（P07）。
 * 件数と対象を必ず含め、取り消せないことを明記する。
 */
export const ConfirmDialog = ({
  open,
  onOpenChange,
  title,
  summary,
  detail,
  confirmText,
  cancelText = messages.common.cancel,
  /** 入力を要求する場合の照合文字列 */
  requireTyping,
  irreversible,
  onConfirm,
  isPending,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  summary: string;
  detail?: ReactNode;
  confirmText: string;
  cancelText?: string;
  requireTyping?: string;
  irreversible?: boolean;
  onConfirm: () => void;
  isPending?: boolean;
}) => {
  const [typed, setTyped] = useState('');
  const canConfirm = !requireTyping || typed.trim() === requireTyping;

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setTyped('');
        onOpenChange(next);
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{summary}</DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-3">
          {detail}
          {irreversible && (
            <p className="flex items-start gap-2 rounded-control border-2 border-foreground p-3 text-body font-semibold text-foreground">
              <AlertTriangle aria-hidden className="mt-0.5 size-5 shrink-0" />
              {messages.common.irreversible}
            </p>
          )}
          {requireTyping && (
            <div className="space-y-1.5">
              <label htmlFor="confirm-typing" className="text-body text-foreground-muted">
                続けるには「{requireTyping}」と入力してください
              </label>
              <Input
                id="confirm-typing"
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
              />
            </div>
          )}
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" size="lg" onClick={() => onOpenChange(false)}>
            {cancelText}
          </Button>
          <Button
            variant={irreversible ? 'destructive' : 'default'}
            size="lg"
            onClick={onConfirm}
            disabled={!canConfirm || isPending}
            disabledReason={!canConfirm ? '確認の入力が一致していません' : undefined}
          >
            {irreversible && <AlertTriangle aria-hidden />}
            {isPending ? messages.common.loading : confirmText}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * ジョブ進捗（docs/09-design-system.md 5.3 JobProgress）。
 * 「ジョブ」「キュー」は画面では「処理」と言い換える。
 */
export const JobProgress = ({
  label,
  done,
  total,
  status,
  onCancel,
  href,
  className,
}: {
  label: string;
  done: number;
  total: number;
  status?: string;
  onCancel?: () => void;
  href?: string;
  className?: string;
}) => {
  const percent = total > 0 ? Math.min(Math.round((done / total) * 100), 100) : 0;
  const isRunning = status === 'running' || status === 'queued';

  return (
    <div className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-1.5 text-body text-foreground">
          {isRunning && <Loader aria-hidden className="size-4 shrink-0 animate-spin" />}
          <span className="truncate">{label}</span>
        </span>
        <span className="tabular shrink-0 text-note text-foreground-muted">
          {messages.jobs.progressOf(done, total)}
        </span>
      </div>

      <div
        className="h-2 w-full overflow-hidden rounded-control bg-muted"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={`${label} の進捗`}
      >
        <div className="h-full bg-foreground transition-all" style={{ width: `${percent}%` }} />
      </div>

      {(onCancel || href) && (
        <div className="flex items-center gap-2">
          {href && (
            <Button variant="link" size="sm" asChild>
              <Link to={href}>{messages.common.detail}</Link>
            </Button>
          )}
          {onCancel && (
            <Button variant="outline" size="sm" onClick={onCancel}>
              <Square aria-hidden className="size-3.5" />
              {messages.jobs.cancel}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

/**
 * 集計期間の明示（docs/09-design-system.md 5.3 PeriodBanner）。
 * 送信期間・観測期間・計測中件数を常時表示する。
 */
export const PeriodBanner = ({
  from,
  to,
  observationDays,
  measuringCount,
  className,
}: {
  from: string;
  to: string;
  observationDays: number;
  measuringCount: number;
  className?: string;
}) => (
  <p
    className={cn(
      'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-card border border-border bg-surface px-3 py-2 text-note text-foreground-muted',
      className,
    )}
  >
    <span>
      {messages.analytics.sendPeriod} <span className="tabular">{from}</span>–
      <span className="tabular">{to}</span>
    </span>
    <span aria-hidden>・</span>
    <span>
      {messages.analytics.observationPeriod}{' '}
      {messages.analytics.observationDaysLabel(observationDays)}
    </span>
    {measuringCount > 0 && (
      <>
        <span aria-hidden>・</span>
        <span className="font-semibold text-foreground">
          {messages.analytics.measuring(measuringCount)}
        </span>
      </>
    )}
  </p>
);

/**
 * 指標カード（docs/09-design-system.md 5.3 MetricCard）。
 * 数値は tabular-nums で桁を揃える。
 */
export const MetricCard = ({
  label,
  value,
  unit,
  delta,
  note,
  onClick,
  isReference,
  className,
}: {
  label: string;
  value: string;
  unit?: string;
  delta?: string;
  note?: string;
  onClick?: () => void;
  isReference?: boolean;
  className?: string;
}) => {
  const content = (
    <>
      <p className="text-note text-foreground-muted">{label}</p>
      <p className="tabular text-metric-sm text-foreground lg:text-metric">
        {value}
        {unit && <span className="ml-1 text-body font-normal text-foreground-muted">{unit}</span>}
      </p>
      <div className="flex flex-wrap items-center gap-x-2 text-note text-foreground-muted">
        {delta && <span className="tabular">{delta}</span>}
        {note && <span>{note}</span>}
        {isReference && (
          <span className="rounded-control border border-dashed border-border-strong px-1.5">
            {messages.analytics.reference}
          </span>
        )}
      </div>
    </>
  );

  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cn(
          'flex flex-col gap-1 rounded-card border border-border bg-surface p-4 text-left',
          'transition-colors duration-hover hover:bg-muted',
          className,
        )}
      >
        {content}
      </button>
    );
  }

  return (
    <div className={cn('flex flex-col gap-1 rounded-card border border-border bg-surface p-4', className)}>
      {content}
    </div>
  );
};

/** 件数表示の共通体裁 */
export const CountText = ({ value, unit = '件' }: { value: number; unit?: string }) => (
  <span className="tabular">
    {formatNumber(value)} {unit}
  </span>
);
